import React from "react";
import { Alert, Box, Button, Stack, Text } from "braid-design-system";
import { useLocation } from "react-router-dom";
import Loading from "../components/Loading";
import { getAllVendors } from "../services/vendorService";
import { getAllCustomers } from "../services/customerService";
import { getSystemSetup } from "../services/setupService";
import { getLocationByCode, getLocations } from "../services/locationService";
import { getAllSalesReps } from "../services/salesRepService";
import {
    createDocument,
    getTransactionSetups,
    TransactionSetupResponse,
    headerFieldMapping,
    getItemLot,
    getDocumentDetails,
    getDocumentHeader,
    stageTransactionLine,
    getAllTempDocuments,
    getTempLinesForDocument,
    getRecentDocuments,
    searchItemLots,
    generateTransactionReportPdf,
} from "../services/transactionService";
import { useToast } from "../hooks/useToast";
import { isZeroNumericString, sanitizeNumericString } from "../utils/numericInput";
import "../styles/TransactionScreen.css";
import TransactionHeaderSection from "./transaction-screen/TransactionHeaderSection";
import TransactionItemsSection from "./transaction-screen/TransactionItemsSection";
import TransactionTotalsSection from "./transaction-screen/TransactionTotalsSection";
import TransactionHistoryViewer from "./transaction-screen/TransactionHistoryViewer";
import TransactionPaymentSection from "./transaction-screen/TransactionPaymentSection";
import TransactionActionsBar from "./transaction-screen/TransactionActionsBar";
import type { FieldConfig, LineColumnConfig, SetupFlag, TransactionLine } from "./transaction-screen/transactionScreenTypes";

interface LocationOption {
    value: string;
    label: string;
}

interface ItemSearchResult extends Record<string, any> {
    productCode?: string;
    stockCode?: string;
    description?: string;
    group?: string;
    unit?: string;
    caseSize?: number | string;
    cost?: number | string;
    selling?: number | string;
    avgCost?: number | string;
    sih?: number | string;
    lotCode?: string;
}

const getTodayDateValue = (): string => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const createDefaultHeaderData = (locationValues: { from?: string; to?: string } = {}): Record<string, string> => ({
    date: getTodayDateValue(),
    from: locationValues.from ?? "",
    to: locationValues.to ?? "",
});

const getPrimaryLocationOption = (locations: LocationOption[], storeCode: string): LocationOption | null => {
    const normalizedStoreCode = String(storeCode ?? "").trim();

    if (normalizedStoreCode) {
        const matchedLocation = locations.find((location) => location.value === normalizedStoreCode);
        if (matchedLocation) {
            return matchedLocation;
        }

        return { value: normalizedStoreCode, label: normalizedStoreCode };
    }

    return locations[0] || null;
};

const getItemSearchCode = (item: ItemSearchResult): string =>
    String(item.productCode ?? item.stockCode ?? item.lotCode ?? "").trim();

const getItemSearchTitle = (item: ItemSearchResult): string =>
    String(item.description ?? item.stockCode ?? item.productCode ?? "Unknown Item").trim();

const normalizeSetupIdentifier = (value: unknown): string => String(value ?? "").trim().toLowerCase();

const normalizeDateInputValue = (value: unknown): string => {
    const rawValue = String(value ?? "").trim();

    if (!rawValue) {
        return "";
    }

    const dateOnlyMatch = rawValue.match(/^\d{4}-\d{2}-\d{2}/);
    if (dateOnlyMatch) {
        return dateOnlyMatch[0];
    }

    const parsedDate = new Date(rawValue);
    if (!Number.isNaN(parsedDate.getTime())) {
        const year = parsedDate.getFullYear();
        const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
        const day = String(parsedDate.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    }

    return rawValue;
};

const getRequestedSetupIdentifier = (search: string): string => {
    const params = new URLSearchParams(search);
    return normalizeSetupIdentifier(params.get("setup") ?? params.get("setup_no") ?? "");
};

const setupMatchesRequestedIdentifier = (setup: TransactionSetupResponse, requestedIdentifier: string): boolean => {
    if (!requestedIdentifier) {
        return false;
    }

    const values = [setup.no, setup.desc, setup.type, setup.menukeyname, setup.menuitem];
    return values.some((value) => normalizeSetupIdentifier(value) === requestedIdentifier);
};

const getLocationSetupMode = (setup: TransactionSetupResponse | null): "transfer-in" | "transfer-out" | "standard" => {
    const fromFixed = isEnabled(setup, "fromlocsetup");
    const toFixed = isEnabled(setup, "tolocsetup");

    if (fromFixed && !toFixed) {
        return "transfer-out";
    }

    if (toFixed && !fromFixed) {
        return "transfer-in";
    }

    // Fallback for legacy/seed data where both flags are equal.
    const typeCode = String(setup?.type ?? "").trim().toUpperCase();
    const menuItem = String(setup?.menuitem ?? "").trim().toUpperCase();
    const setupDesc = String(setup?.desc ?? "").trim().toLowerCase();

    if (
        typeCode === "TRI"
        || menuItem.includes("TRIN")
        || setupDesc.includes("transfer in")
    ) {
        return "transfer-in";
    }

    if (
        typeCode === "TRO"
        || menuItem.includes("TROUT")
        || setupDesc.includes("transfer out")
    ) {
        return "transfer-out";
    }

    return "standard";
};

/**
 * Get the stock location from setup for transfer transactions
 */
const getStockLocation = (setup: TransactionSetupResponse | null): string => {
    return String(setup?.stocklocation ?? "").trim();
};

/**
 * Get location label from locations list by code
 */
const getLocationLabel = (locations: LocationOption[], locCode: string): string => {
    const normalizedCode = String(locCode ?? "").trim();
    if (!normalizedCode) {
        return "";
    }

    const location = locations.find((loc) => loc.value === normalizedCode);
    if (location) {
        return location.label;
    }

    // Fallback to code if location not found in list
    return normalizedCode;
};

const getLocationFieldOptions = (
    setup: TransactionSetupResponse | null,
    fieldKey: "from" | "to",
    locations: LocationOption[],
    storeCode: string
): LocationOption[] => {
    const mode = getLocationSetupMode(setup);
    const stockLocation = getStockLocation(setup);

    // For transfer transactions, use stocklocation as the fixed location
    if (mode === "transfer-out" && stockLocation) {
        // From is fixed to stocklocation, To shows all other locations
        if (fieldKey === "from") {
            const stockLocationLabel = getLocationLabel(locations, stockLocation);
            return [{ value: stockLocation, label: stockLocationLabel || stockLocation }];
        }
        // To field shows all locations except stocklocation
        return locations.filter((loc) => loc.value !== stockLocation);
    }

    if (mode === "transfer-in" && stockLocation) {
        // To is fixed to stocklocation, From shows all other locations
        if (fieldKey === "to") {
            const stockLocationLabel = getLocationLabel(locations, stockLocation);
            return [{ value: stockLocation, label: stockLocationLabel || stockLocation }];
        }
        // From field shows all locations except stocklocation
        return locations.filter((loc) => loc.value !== stockLocation);
    }

    // Standard mode or no stocklocation configured - use original logic
    const primaryLocation = getPrimaryLocationOption(locations, storeCode);
    const otherLocations = locations.filter((location) => location.value !== primaryLocation?.value);

    if (fieldKey === "from") {
        return primaryLocation ? [primaryLocation] : [];
    }

    if (fieldKey === "to") {
        return primaryLocation ? otherLocations : locations;
    }

    return primaryLocation ? [primaryLocation] : locations;
};

const getDefaultLocationHeaderValues = (
    setup: TransactionSetupResponse | null,
    locations: LocationOption[],
    storeCode: string
): { from: string; to: string } => {
    const mode = getLocationSetupMode(setup);
    const stockLocation = getStockLocation(setup);

    // For transfer transactions, pre-populate the fixed field with stocklocation
    if (mode === "transfer-out" && stockLocation) {
        return {
            from: stockLocation,
            to: "",
        };
    }

    if (mode === "transfer-in" && stockLocation) {
        return {
            from: "",
            to: stockLocation,
        };
    }

    // Standard mode or no stocklocation configured - use original logic
    const primaryLocation = getPrimaryLocationOption(locations, storeCode);

    return {
        from: primaryLocation?.value ?? String(storeCode ?? "").trim(),
        to: "",
    };
};

// Each element is shown when its pos_trasetup flag column is 1. Elements
// without a flag are always shown in every transaction screen.
const headerFields: FieldConfig[] = [
    { key: "docNo", label: "Document No" },
    { key: "date", label: "Date", type: "date" },
    { key: "supplier", label: "Supplier", flag: "supplier", type: "select", options: ["Select supplier"] },
    { key: "from", label: "From", flag: "sourcetra", type: "select", options: ["Main location"] },
    { key: "customer", label: "Customer", flag: "customer", type: "select", options: ["Walk-in customer"] },
    { key: "to", label: "To", flag: "desttra", type: "select", options: ["Destination location"] },
    { key: "assistant", label: "Sales Rep", flag: "assistant", type: "select", options: ["Select sales rep"] },
    { key: "transactionMode", label: "Transaction Mode", flag: "showtransmode", type: "select", options: ["Normal"] },
    { key: "refTrnNo", label: "Ref. Trans No", flag: "showreftrnno" },
    { key: "validityDate", label: "Validity Date", flag: "showvalidity", type: "date" },
    { key: "priceMode", label: "Price Mode", flag: "pricemode", type: "select", options: ["Cost", "Selling", "Average"] },
    { key: "paymentMode", label: "Payment Mode", flag: "showpaymode", type: "select", options: ["Cash", "Credit", "Card"] },
    { key: "reorderBased", label: "Reorder Point Based", flag: "showreorder", type: "checkbox" },
];

// Hidden columns only hide: their values are still carried on each line and saved.
const lineColumns: LineColumnConfig[] = [
    { key: "productCode", label: "Product Code", source: "pos_itemlots", editable: true },
    { key: "stockCode", label: "Stock Code", flag: "showstockcode", source: "pos_itemlots", editable: false },
    { key: "description", label: "Description", source: "pos_itemlots", editable: false },
    { key: "group", label: "Group", source: "pos_itemlots", editable: false },
    { key: "unit", label: "Unit", flag: "showunit", source: "pos_itemlots", editable: false },
    { key: "caseSize", label: "Case Size", flag: "showcase", type: "number", source: "pos_itemlots", editable: false },
    { key: "cost", label: "Cost", flag: "showcost", type: "number", editable: true },
    { key: "selling", label: "Selling", flag: "showprice", type: "number", editable: true },
    { key: "avgCost", label: "Avg Cost", flag: "calavg", type: "number", source: "pos_itemlots", editable: false },
    { key: "sih", label: "SIH", type: "number", decimalPlaces: 3, source: "pos_itemlots", editable: false },
    { key: "caseQty", label: "Case Qty", flag: "caseqty", type: "number", decimalPlaces: 3, source: "pos_item", editable: true },
    { key: "balanceQty", label: "Balance Qty", flag: "showbalqty", type: "number", decimalPlaces: 3, editable: false },
    { key: "unitQty", label: "Unit Qty", type: "number", decimalPlaces: 3, editable: true },
    { key: "freeQty", label: "Free Qty", flag: "freeqty", type: "number", decimalPlaces: 3, editable: true },
    { key: "expiryDate", label: "Expiry Date", flag: "showexpdate", type: "date", editable: true },
    { key: "discountPer", label: "Discount %", flag: "linedisper", type: "number", editable: true },
    { key: "discountAmount", label: "Discount Amt", flag: "linedisamt", type: "number", editable: true },
    { key: "amount", label: "Amount", flag: "showamount", type: "number", editable: false },
    { key: "remarks", label: "Remarks", flag: "showremarks", editable: true },
];

const totalFields: FieldConfig[] = [
    { key: "gross", label: "Gross Amount", flag: "showgross", type: "number" },
    { key: "netDiscount", label: "Net Discount %", flag: "netdisper", type: "number" },
    { key: "netDiscountAmount", label: "Net Discount", flag: "netdisamt", type: "number" },
    { key: "addition", label: "Addition", flag: "addition", type: "number" },
    { key: "deduction", label: "Deduction", flag: "deduction", type: "number" },
    { key: "due", label: "Due Amount", flag: "showdue", type: "number" },
    { key: "tax", label: "Tax", flag: "tax", type: "number" },
    { key: "net", label: "Net Amount", flag: "shownet", type: "number" },
];

const emptyLine = (id: number): TransactionLine => ({
    id,
    productCode: "",
    stockCode: "",
    description: "",
    group: "",
    unit: "",
    caseSize: "0",
    cost: "0.00",
    selling: "0.00",
    avgCost: "0.00",
    sih: "0",
    caseQty: "0",
    balanceQty: "0",
    unitQty: "0",
    freeQty: "0",
    expiryDate: "",
    discountPer: "0",
    discountAmount: "0.00",
    tax: "0.00",
    amount: "0.00",
    remarks: "",
    lotCode: "",
});

const isTruthyFlagValue = (value: unknown): boolean => {
    if (value === true) {
        return true;
    }

    if (typeof value === "number") {
        return value !== 0;
    }

    if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (["1", "true", "yes", "y"].includes(normalized)) {
            return true;
        }

        if (normalized.length === 1 && normalized.charCodeAt(0) === 1) {
            return true;
        }
    }

    return false;
};

const isEnabled = (setup: TransactionSetupResponse | null, flag?: SetupFlag): boolean => {
    if (!flag) {
        return true;
    }

    const value = setup?.[flag];
    return isTruthyFlagValue(value);
};

const parseNumeric = (value: unknown): number => {
    const parsed = typeof value === "number" ? value : parseFloat(String(value ?? "0"));
    return Number.isFinite(parsed) ? parsed : 0;
};

const hasNumericFieldValue = (value: unknown): boolean => String(value ?? "").trim() !== "";

const isProcessedFlagEnabled = (value: unknown): boolean => {
    if (value === true) {
        return true;
    }

    if (typeof value === "number") {
        return value !== 0;
    }

    if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (["1", "true", "yes", "y"].includes(normalized)) {
            return true;
        }

        // MySQL BIT(1) may surface as a single-byte string containing \x01.
        if (normalized.length === 1 && normalized.charCodeAt(0) === 1) {
            return true;
        }
    }

    return false;
};

const HEADER_DB_TO_UI_FIELD_MAPPING = Object.fromEntries(
    Object.entries(headerFieldMapping).map(([uiKey, dbKey]) => [dbKey, uiKey]),
);

const mapDocumentHeaderToUi = (header: Record<string, any>): Record<string, string> => {
    const mappedHeader: Record<string, string> = {};

    Object.entries(header || {}).forEach(([key, value]) => {
        const uiKey = HEADER_DB_TO_UI_FIELD_MAPPING[key] || key;
        mappedHeader[uiKey] = value == null ? "" : String(value);
    });

    mappedHeader.docNo = String(header?.DocNo ?? header?.docNo ?? "");
    mappedHeader.date = normalizeDateInputValue(header?.trhed_date ?? header?.date ?? mappedHeader.date);
    mappedHeader.priceMode = String(header?.trhed_pricemode ?? header?.priceMode ?? mappedHeader.priceMode ?? "");

    return mappedHeader;
};

const mapDocumentDetailToLine = (row: Record<string, any>, index: number): TransactionLine => ({
    id: row.id || Date.now() + index,
    productCode: row.trdet_procode || row.productCode || "",
    stockCode: row.trdet_stockcode || row.stockCode || "",
    description: row.trdet_prodesc || row.description || "",
    group: row.trdet_group || row.group || "",
    unit: row.trdet_unit || row.unit || "",
    caseSize: String(row.trdet_casesize || row.caseSize || 0),
    cost: String(row.trdet_cprice || row.cost || 0),
    selling: String(row.trdet_sprice || row.selling || 0),
    avgCost: String(row.trdet_aprice || row.avgCost || 0),
    sih: String(row.trdet_sih || row.sih || 0),
    caseQty: String(row.trdet_caseqty || row.caseQty || 0),
    balanceQty: String(row.trdet_balqty || row.balanceQty || 0),
    unitQty: String(row.trdet_unitqty || row.unitQty || 0),
    freeQty: String(row.trdet_freeqty || row.freeQty || 0),
    expiryDate: row.trdet_expdate || row.expiryDate || "",
    discountPer: String(row.trdet_disper || row.discountPer || 0),
    discountAmount: String(row.trdet_disamt || row.discountAmount || 0),
    tax: String(row.trdet_taxamt || row.tax || 0),
    amount: String(row.trdet_amount || row.amount || 0),
    remarks: row.trdet_remarks || row.remarks || "",
    lotCode: row.trdet_lot || row.lotCode || row.trdet_stockcode || row.stockCode || "",
});

const getUniqueRecentDocuments = (recentDocs: Record<string, any>[]): Record<string, any>[] => (
    Array.from(
        new Map(
            (recentDocs || [])
                .filter((doc) => String(doc?.trhed_runid ?? doc?.trhedRunId ?? doc?.DocNo ?? doc?.docNo ?? "").trim().length > 0)
                .map((doc) => [String(doc.trhed_runid ?? doc.trhedRunId ?? doc.DocNo ?? doc.docNo), doc]),
        ).values(),
    )
);

const TransactionScreen: React.FC = () => {
    const toast = useToast();
    const location = useLocation();
    const [setups, setSetups] = React.useState<TransactionSetupResponse[]>([]);
    const [selectedNo, setSelectedNo] = React.useState<string>("");
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [lines, setLines] = React.useState<TransactionLine[]>([emptyLine(1)]);
    const [headerData, setHeaderData] = React.useState<Record<string, string>>({});
    const [currentDocumentNo, setCurrentDocumentNo] = React.useState<string>("");
    const [tempNo, setTempNo] = React.useState<string>("");
    const [isReadOnlyProcessedDocument, setIsReadOnlyProcessedDocument] = React.useState(false);
    const [stagingLineId, setStagingLineId] = React.useState<number | null>(null);
    const [stagedLineIds, setStagedLineIds] = React.useState<Record<number, boolean>>({});
    const [activeLineId, setActiveLineId] = React.useState<number | null>(1);
    const [itemSearchModalOpen, setItemSearchModalOpen] = React.useState(false);
    const [itemSearchTerm, setItemSearchTerm] = React.useState("");
    const [itemSearchResults, setItemSearchResults] = React.useState<ItemSearchResult[]>([]);
    const [itemSearchLoading, setItemSearchLoading] = React.useState(false);
    const itemSearchRequestRef = React.useRef(0);
    const cleanSnapshotRef = React.useRef<string>("");
    const autoRestoredTempTypesRef = React.useRef<Record<string, boolean>>({});
    const [suppliers, setSuppliers] = React.useState<Array<{ code: string; label: string }>>([]);
    const [customers, setCustomers] = React.useState<Array<{ code: string; label: string }>>([]);
    const [salesReps, setSalesReps] = React.useState<Array<{ id: number; name: string; userName: string }>>([]);
    const [locationOptions, setLocationOptions] = React.useState<LocationOption[]>([]);
    const [storeLocationCode, setStoreLocationCode] = React.useState<string>("");
    const [setupTaxPercentage, setSetupTaxPercentage] = React.useState<number>(0);
    const [documents, setDocuments] = React.useState<Array<Record<string, any>>>([]);
    const [isLoadingDocument, setIsLoadingDocument] = React.useState(false);

    const handleRestoreTempDocument = React.useCallback(async (tempNo: string, transactionType?: string | null) => {
        try {
            setLoading(true);
            setIsLoadingDocument(true);
            const tempLines = await getTempLinesForDocument(tempNo, transactionType);

            // Convert database rows to TransactionLine format
            const restoredLines: TransactionLine[] = tempLines.map((row: any, index: number) => mapDocumentDetailToLine(row, index));

            setLines(restoredLines);
            setTempNo(tempNo);
            setIsReadOnlyProcessedDocument(false);
            toast.showSuccess(`Restored ${restoredLines.length} lines from temporary document ${tempNo}`);
        } catch (error: any) {
            toast.showError(`Failed to restore temporary document: ${error?.message || error}`);
        } finally {
            setLoading(false);
            setIsLoadingDocument(false);
        }
    }, [toast]);

    React.useEffect(() => {
        if (lines.length === 0) {
            setActiveLineId(null);
            return;
        }

        if (activeLineId == null || !lines.some((line) => line.id === activeLineId)) {
            setActiveLineId(lines[0].id);
        }
    }, [activeLineId, lines]);

    React.useEffect(() => {
        const loadBootstrapData = async () => {
            try {
                setLoading(true);
                setError(null);
                const [data, locations, systemSetup, reps] = await Promise.all([
                    getTransactionSetups(),
                    getLocations().catch(() => []),
                    getSystemSetup().catch(() => null),
                    getAllSalesReps().catch(() => []),
                ]);

                const requestedSetup = getRequestedSetupIdentifier(location.search);
                const matchedSetup = requestedSetup
                    ? data.find((setup) => setupMatchesRequestedIdentifier(setup, requestedSetup))
                    : undefined;

                const storeId = String(systemSetup?.setup_storeid ?? "").trim();
                setStoreLocationCode(storeId);
                setSetupTaxPercentage(parseNumeric(systemSetup?.setup_tax));

                const mappedLocations = locations
                    .map((location) => ({
                        value: String(location.loc_code ?? "").trim(),
                        label: String(location.loc_desc ?? location.loc_code ?? "").trim() || String(location.loc_code ?? "").trim(),
                    }))
                    .filter((location) => Boolean(location.value));

                if (mappedLocations.length > 0) {
                    setLocationOptions(mappedLocations);
                } else if (storeId) {
                    try {
                        const location = await getLocationByCode(storeId);
                        const locationCode = String(location.loc_code ?? storeId).trim();
                        const locationLabel = String(location.loc_desc ?? locationCode).trim() || locationCode;
                        setLocationOptions([{ value: locationCode, label: locationLabel }]);
                    } catch {
                        setLocationOptions([{ value: storeId, label: storeId }]);
                    }
                } else {
                    setLocationOptions([]);
                }

                setSalesReps(
                    reps.map((rep) => ({
                        id: rep.id || 0,
                        name: rep.name || "",
                        userName: rep.user_name || "",
                    })),
                );

                setSetups(data);
                setSelectedNo(
                    matchedSetup?.no != null
                        ? String(matchedSetup.no)
                        : data[0]?.no != null
                            ? String(data[0].no)
                            : "",
                );
            } catch (loadError: any) {
                const message = loadError?.message || "Unable to load transaction setup.";
                setError(message);
                toast.showError(message);
            } finally {
                setLoading(false);
            }
        };

        loadBootstrapData();
    }, [location.search, toast]);

    const selectedSetup = React.useMemo(
        () => setups.find((setup) => String(setup.no) === selectedNo) || null,
        [selectedNo, setups],
    );

    // Stock adjustment notes count into the open initialization batch at the
    // setup's stock location, and their line SIH is that batch's snapshot
    // (itemlots_sih_image) rather than the live stock in hand.
    const isStockAdjustment = String(selectedSetup?.type ?? "").trim().toUpperCase() === "ADJ";

    // For stock transfers the line data (SIH, cost, selling, ...) must reflect the
    // selected "from" branch that stock is being pulled out of - not the user's
    // own branch. Stock adjustments use the setup's stock location. Other
    // transaction types keep the default (active branch).
    const lineLookupLocation = React.useMemo<string | undefined>(() => {
        const mode = getLocationSetupMode(selectedSetup);
        if (mode === "transfer-in" || mode === "transfer-out") {
            return String(headerData.from ?? "").trim() || undefined;
        }
        if (isStockAdjustment) {
            return getStockLocation(selectedSetup) || undefined;
        }
        return undefined;
    }, [selectedSetup, headerData.from, isStockAdjustment]);

    const getLineSih = React.useCallback(
        (itemLotData: Record<string, any>) =>
            (isStockAdjustment ? itemLotData?.sihImage : itemLotData?.sih) ?? 0,
        [isStockAdjustment],
    );

    const refreshRecentDocuments = React.useCallback(async (setup: TransactionSetupResponse) => {
        if (!setup.headertable) {
            setDocuments([]);
            return;
        }

        const recentDocs = await getRecentDocuments(setup.no, 10);
        setDocuments(getUniqueRecentDocuments(recentDocs));
    }, []);

    const resetTransactionDocument = React.useCallback(() => {
        const resetHeaderData = createDefaultHeaderData(getDefaultLocationHeaderValues(selectedSetup, locationOptions, storeLocationCode));
        const resetLines = [emptyLine(1)];

        setHeaderData(resetHeaderData);
        setLines(resetLines);
        setCurrentDocumentNo("");
        setTempNo("");
        setIsReadOnlyProcessedDocument(false);
        setStagedLineIds({});
        setStagingLineId(null);

        cleanSnapshotRef.current = JSON.stringify({
            selectedNo,
            headerData: resetHeaderData,
            lines: resetLines,
            currentDocumentNo: "",
        });
    }, [locationOptions, selectedNo, selectedSetup, storeLocationCode]);

    // Reset form when setup changes
    React.useEffect(() => {
        resetTransactionDocument();
        setDocuments([]);
    }, [resetTransactionDocument]);

    React.useEffect(() => {
        if (!selectedSetup?.type) {
            return;
        }

        const transactionType = String(selectedSetup.type).trim().toUpperCase();
        if (!transactionType || autoRestoredTempTypesRef.current[transactionType]) {
            return;
        }

        autoRestoredTempTypesRef.current[transactionType] = true;

        const autoRestoreTempDocuments = async () => {
            try {
                const tempDocs = await getAllTempDocuments(transactionType);
                if (tempDocs && tempDocs.length > 0) {
                    const firstTempDoc = tempDocs[0].temp_no;
                    await handleRestoreTempDocument(firstTempDoc, transactionType);
                }
            } catch (err: any) {
                // Silently fail - it's not critical
            }
        };

        autoRestoreTempDocuments();
    }, [handleRestoreTempDocument, selectedSetup?.type]);

    React.useEffect(() => {
        if (!setups.length) {
            return;
        }

        const requestedSetup = getRequestedSetupIdentifier(location.search);
        if (!requestedSetup) {
            return;
        }

        const matchedSetup = setups.find((setup) => setupMatchesRequestedIdentifier(setup, requestedSetup));
        if (matchedSetup && String(matchedSetup.no) !== selectedNo) {
            setSelectedNo(String(matchedSetup.no));
        }
    }, [location.search, selectedNo, setups]);

    // Load supplier, customer, and recent documents when a setup is selected
    React.useEffect(() => {
        if (!selectedSetup) {
            return;
        }

        const setup = selectedSetup;

        const loadPartiesAndDocuments = async () => {
            try {
                const vendors = await getAllVendors();
                setSuppliers(
                    vendors.map((v) => ({ code: v.ven_code || String(v.ven_id || ""), label: (v.ven_code ? v.ven_code + " - " : "") + (v.ven_Name || "") })),
                );

                const custs = await getAllCustomers();
                setCustomers(
                    custs.map((c) => ({ code: c.cus_code || String(c.cus_id || ""), label: (c.cus_code ? c.cus_code + " - " : "") + (c.cus_name || "") })),
                );

                await refreshRecentDocuments(setup);
            } catch (err: any) {
                // Non-fatal; show toast but allow user to continue
                toast.showError(`Failed to load data: ${err?.message || err}`);
            }
        };

        loadPartiesAndDocuments();
    }, [refreshRecentDocuments, selectedSetup, toast]);

    // Recalculate amounts when price mode changes, but preserve discounts
    // Skip recalculation when loading a document from DB - use DB values as-is
    React.useEffect(() => {
        if (isLoadingDocument) {
            return;
        }

        setLines((currentLines) =>
            currentLines.map((line) => {
                if (parseFloat(String(line.unitQty || 0)) > 0) {
                    const priceVal = getPriceByMode(line, headerData.priceMode);
                    const unitQtyVal = parseFloat(String(line.unitQty || "0")) || 0;
                    const discountPct = parseFloat(line.discountPer) || 0;
                    const discountAmt = parseFloat(line.discountAmount) || 0;

                    const grossAmount = priceVal * unitQtyVal;
                    const discountFromPercentage = grossAmount * (discountPct / 100);
                    const totalDiscount = discountFromPercentage + discountAmt;
                    const finalAmount = Math.max(0, grossAmount - totalDiscount);

                    return {
                        ...line,
                        amount: finalAmount.toFixed(2),
                    };
                }
                return line;
            }),
        );
    }, [headerData.priceMode, isLoadingDocument]);

    const currentFormSnapshot = React.useMemo(
        () =>
            JSON.stringify({
                selectedNo,
                headerData,
                lines,
                currentDocumentNo,
            }),
        [selectedNo, headerData, lines, currentDocumentNo],
    );

    const hasFormChanges = currentFormSnapshot !== cleanSnapshotRef.current;

    const requireFormChanges = (actionLabel: string): boolean => {
        if (hasFormChanges) {
            return true;
        }

        const normalizedAction = actionLabel.toLowerCase();

        if ((normalizedAction === "saving" || normalizedAction === "temporary save") && Boolean(tempNo)) {
            return true;
        }

        if (normalizedAction === "printing" && isReadOnlyProcessedDocument && Boolean(currentDocumentNo)) {
            return true;
        }

        toast.showInfo(`Add or change at least one field before ${actionLabel.toLowerCase()}.`);
        return false;
    };

    /**
     * Header fields, grid columns and totals: hide elements whose pos_trasetup
     * flag is 0. Elements without a flag are always shown.
     */
    const visibleHeaderFields = React.useMemo(
        () => headerFields.filter((field) => isEnabled(selectedSetup, field.flag)),
        [selectedSetup],
    );

    const visibleLineColumns = React.useMemo(
        () => lineColumns.filter((column) => isEnabled(selectedSetup, column.flag)),
        [selectedSetup],
    );

    const visibleTotalFields = React.useMemo(
        () => totalFields.filter((field) => isEnabled(selectedSetup, field.flag)),
        [selectedSetup],
    );

    const selectedSetupTitle = React.useMemo(() => {
        if (!selectedSetup) {
            return "Transaction Setup";
        }

        return String(selectedSetup.desc || selectedSetup.menuitem || selectedSetup.type || `Setup ${selectedSetup.no}`);
    }, [selectedSetup]);

    const computedTotals = React.useMemo(() => {
        const gross = lines.reduce((sum, line) => sum + parseNumeric(line.amount), 0);
        const netDiscountPer = parseNumeric(headerData.netDiscount);
        const manualDiscountAmount = parseNumeric(headerData.netDiscountAmount);
        const percentDiscountAmount = gross * (netDiscountPer / 100);
        const totalDiscountAmount = percentDiscountAmount + manualDiscountAmount;
        const additions = parseNumeric(headerData.addition);
        const deductions = parseNumeric(headerData.deduction);
        const tax = hasNumericFieldValue(headerData.tax)
            ? parseNumeric(headerData.tax)
            : gross * (setupTaxPercentage / 100);
        const net = gross - totalDiscountAmount + additions - deductions + tax;

        return {
            gross: gross.toFixed(2),
            // The combined discount actually applied (percentage-derived + any manual flat
            // amount), since the percentage alone never got persisted to trhed_netdiscamt.
            netDiscountAmount: totalDiscountAmount.toFixed(2),
            net: net.toFixed(2),
            tax: tax.toFixed(2),
        };
    }, [lines, headerData.addition, headerData.deduction, headerData.netDiscount, headerData.netDiscountAmount, headerData.tax, setupTaxPercentage]);

    /**
     * Get the price to use for calculation based on the selected price mode
     */
    const getPriceByMode = (line: TransactionLine, priceMode: string = ""): number => {
        const mode = String(priceMode || "").toLowerCase().trim();
        if (mode === "cost") {
            return parseFloat(String(line.cost || "0")) || 0;
        } else if (mode === "average") {
            return parseFloat(String(line.avgCost || "0")) || 0;
        } else if (mode === "selling") {
            return parseFloat(String(line.selling || "0")) || 0;
        } else {
            // Default to cost price when price mode is not selected
            return parseFloat(String(line.cost || "0")) || 0;
        }
    };

    const updateLine = (lineId: number, key: keyof TransactionLine, value: string) => {
        // If discount percentage is being changed, use special handler
        if (key === "discountPer") {
            handleDiscountChange(lineId, value);
            return;
        }

        // If discount amount is being changed, use special handler
        if (key === "discountAmount") {
            handleDiscountAmountChange(lineId, value);
            return;
        }

        setStagedLineIds((prev) => {
            if (!prev[lineId]) {
                return prev;
            }
            const next = { ...prev };
            delete next[lineId];
            return next;
        });

        setLines((currentLines) =>
            currentLines.map((line) => {
                if (line.id !== lineId) return line;

                const updatedLine = { ...line, [key]: value } as TransactionLine;

                // Recalculate amount when unitQty or price-related fields change
                try {
                    // Trigger recalculation if unitQty or any price field changes
                    if (["unitQty", "selling", "cost", "avgCost"].includes(key)) {
                        const priceVal = getPriceByMode(updatedLine, headerData.priceMode);
                        const unitQtyVal = parseFloat(
                            key === "unitQty" ? value : String(updatedLine.unitQty || "0"),
                        ) || 0;
                        const discountPct = parseFloat(updatedLine.discountPer) || 0;
                        const discountAmt = parseFloat(updatedLine.discountAmount) || 0;

                        const grossAmount = priceVal * unitQtyVal;
                        const discountFromPercentage = grossAmount * (discountPct / 100);
                        const totalDiscount = discountFromPercentage + discountAmt;
                        const finalAmount = Math.max(0, grossAmount - totalDiscount);

                        updatedLine.amount = finalAmount.toFixed(2);
                    }
                } catch (err) {
                    // ignore parse errors and leave amount as-is
                }

                return updatedLine;
            }),
        );
    };

    const applyItemLotDataToLine = React.useCallback(
        (lineId: number, itemLotData: Record<string, any>, productCodeOverride?: string) => {
            setLines((currentLines) =>
                currentLines.map((line) => {
                    if (line.id !== lineId) {
                        return line;
                    }

                    const nextLine = {
                        ...line,
                        productCode: String(productCodeOverride ?? itemLotData?.productCode ?? itemLotData?.stockCode ?? line.productCode ?? "").trim(),
                        stockCode: String(itemLotData?.stockCode ?? ""),
                        description: String(itemLotData?.description ?? ""),
                        group: String(itemLotData?.group ?? ""),
                        unit: String(itemLotData?.unit ?? ""),
                        caseSize: String(itemLotData?.caseSize ?? 0),
                        cost: String(itemLotData?.cost ?? 0),
                        selling: String(itemLotData?.selling ?? 0),
                        avgCost: String(itemLotData?.avgCost ?? 0),
                        sih: String(getLineSih(itemLotData)),
                        lotCode: String(itemLotData?.lotCode ?? itemLotData?.stockCode ?? ""),
                    };

                    const priceVal = getPriceByMode(nextLine, headerData.priceMode);
                    const unitQtyVal = parseFloat(String(line.unitQty || "0")) || 0;
                    const discountPct = parseFloat(nextLine.discountPer) || 0;
                    const discountAmt = parseFloat(nextLine.discountAmount) || 0;

                    const grossAmount = priceVal * unitQtyVal;
                    const discountFromPercentage = grossAmount * (discountPct / 100);
                    const totalDiscount = discountFromPercentage + discountAmt;
                    const finalAmount = Math.max(0, grossAmount - totalDiscount);

                    return {
                        ...nextLine,
                        amount: finalAmount.toFixed(2),
                    };
                }),
            );
        },
        [headerData.priceMode, getLineSih],
    );

    const removeLine = (lineId: number) => {
        setStagedLineIds((prev) => {
            if (!prev[lineId]) {
                return prev;
            }
            const next = { ...prev };
            delete next[lineId];
            return next;
        });

        setLines((currentLines) =>
            currentLines.length === 1 ? [emptyLine(Date.now())] : currentLines.filter((line) => line.id !== lineId),
        );
    };

    const updateHeaderField = (key: string, value: string) => {
        setHeaderData((prev) => ({ ...prev, [key]: value }));
    };

    const handleProductCodeChange = (lineId: number, productCode: string) => {
        updateLine(lineId, "productCode", productCode);
    };

    // Focus a line's input once the current render has committed.
    const focusLineField = (lineId: number, columnKey: keyof TransactionLine) => {
        window.setTimeout(() => {
            document
                .querySelector<HTMLInputElement>(`input[data-line-id="${lineId}"][data-column-key="${columnKey}"]`)
                ?.focus();
        }, 0);
    };

    const selectItemForLine = (lineId: number, itemLotData: Record<string, any>) => {
        applyItemLotDataToLine(lineId, itemLotData, getItemSearchCode(itemLotData) || undefined);
        setActiveLineId(lineId);
        focusLineField(lineId, "unitQty");
    };

    const openItemSearchForLine = (lineId: number, searchText: string) => {
        setActiveLineId(lineId);
        setItemSearchTerm(searchText);
        setItemSearchModalOpen(true);
    };

    /**
     * Enter in Product Code works like the billing barcode box: an exact product
     * code or barcode fills the line, anything else (e.g. part of an item name)
     * opens the item search pre-filled with the typed text.
     */
    const handleProductCodeSubmit = async (lineId: number, productCode: string) => {
        const searchText = productCode.trim();
        if (!searchText) {
            return;
        }

        try {
            const itemLotData = await getItemLot(searchText, undefined, lineLookupLocation);
            if (getItemSearchCode(itemLotData)) {
                selectItemForLine(lineId, itemLotData);
                return;
            }
        } catch {
            // No exact product-code match - try it as a barcode next.
        }

        if (searchText.length >= 2) {
            try {
                const normalized = searchText.toLowerCase();
                const isExact = (value: unknown) => String(value ?? "").trim().toLowerCase() === normalized;
                const results = (await searchItemLots(searchText, undefined, lineLookupLocation)) as ItemSearchResult[];
                const match = results.find((item) => isExact(item.barcode)) || results.find((item) => isExact(item.productCode));
                if (match) {
                    selectItemForLine(lineId, match);
                    return;
                }
            } catch {
                // Fall through to the search modal, which reports its own errors.
            }
        }

        openItemSearchForLine(lineId, searchText);
    };

    React.useEffect(() => {
        if (!itemSearchModalOpen) {
            setItemSearchResults([]);
            setItemSearchLoading(false);
            return;
        }

        const searchText = itemSearchTerm.trim();
        if (searchText.length < 2) {
            setItemSearchResults([]);
            setItemSearchLoading(false);
            return;
        }

        const requestId = ++itemSearchRequestRef.current;
        const timeoutId = window.setTimeout(() => {
            setItemSearchLoading(true);

            searchItemLots(searchText, undefined, lineLookupLocation)
                .then((results) => {
                    if (itemSearchRequestRef.current !== requestId) {
                        return;
                    }

                    setItemSearchResults(results as ItemSearchResult[]);
                    setItemSearchLoading(false);
                })
                .catch((error: any) => {
                    if (itemSearchRequestRef.current !== requestId) {
                        return;
                    }

                    setItemSearchResults([]);
                    setItemSearchLoading(false);
                    toast.showError(error?.message || "Failed to search items.");
                });
        }, 250);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [itemSearchModalOpen, itemSearchTerm, toast, lineLookupLocation]);

    const openItemSearchModal = () => {
        setItemSearchModalOpen(true);
    };

    const closeItemSearchModal = () => {
        setItemSearchModalOpen(false);
        setItemSearchTerm("");
        setItemSearchResults([]);
        setItemSearchLoading(false);
    };

    const handleSelectItemSearchResult = (item: ItemSearchResult) => {
        const targetLineId = activeLineId ?? lines[0]?.id ?? null;
        const itemCode = getItemSearchCode(item);

        if (!targetLineId) {
            toast.showError("No transaction line is available for item selection.");
            return;
        }

        if (!itemCode) {
            toast.showError("Selected item does not include a product code.");
            return;
        }

        updateLine(targetLineId, "productCode", itemCode);
        applyItemLotDataToLine(targetLineId, item, itemCode);
        setActiveLineId(targetLineId);
        closeItemSearchModal();
        focusLineField(targetLineId, "unitQty");
    };

    const recalculateLineAmount = (lineId: number) => {
        setLines((currentLines) =>
            currentLines.map((l) => {
                if (l.id !== lineId) {
                    return l;
                }

                const qty = parseFloat(l.unitQty || l.caseQty || "0") || 0;
                const price = getPriceByMode(l, headerData.priceMode);
                const discountPct = parseFloat(l.discountPer) || 0;
                const discountAmt = parseFloat(l.discountAmount) || 0;

                const grossAmount = qty * price;
                const discountFromPercentage = grossAmount * (discountPct / 100);
                const totalDiscount = discountFromPercentage + discountAmt;
                const finalAmount = Math.max(0, grossAmount - totalDiscount);

                return {
                    ...l,
                    amount: finalAmount.toFixed(2),
                };
            }),
        );
    };

    const handleDiscountChange = async (lineId: number, discountPercentage: string) => {
        // Update just the discount percentage and recalculate amount
        setLines((currentLines) =>
            currentLines.map((l) => {
                if (l.id !== lineId) {
                    return l;
                }
                return {
                    ...l,
                    discountPer: discountPercentage,
                };
            }),
        );
        recalculateLineAmount(lineId);
    };

    const handleDiscountAmountChange = async (lineId: number, discountAmountValue: string) => {
        // Update just the discount amount and recalculate
        setLines((currentLines) =>
            currentLines.map((l) => {
                if (l.id !== lineId) {
                    return l;
                }
                return {
                    ...l,
                    discountAmount: discountAmountValue,
                };
            }),
        );
        recalculateLineAmount(lineId);
    };

    const handleRestoreSelectedDocument = async (docNo: string) => {
        if (!selectedSetup || !docNo.trim()) {
            return;
        }

        try {
            setLoading(true);
            setIsLoadingDocument(true);

            const [header, details] = await Promise.all([
                getDocumentHeader(selectedSetup.no, docNo),
                getDocumentDetails(selectedSetup.no, docNo),
            ]);

            const restoredHeader = mapDocumentHeaderToUi(header || {});

            // If the restored header contains a sales rep username, translate it to the rep id
            if (restoredHeader.assistant) {
                const matchedRep = salesReps.find((r) => r.userName === String(restoredHeader.assistant) || String(r.id) === String(restoredHeader.assistant));
                if (matchedRep) {
                    restoredHeader.assistant = String(matchedRep.id);
                }
            }
            const restoredLines = (details || []).map((row: any, index: number) => mapDocumentDetailToLine(row, index));
            const isProcessedDocument = isProcessedFlagEnabled(header?.trhed_process ?? header?.trhedProcess);
            const restoredDocNo = String(header?.DocNo ?? header?.docNo ?? docNo);
            const restoredTempNo = String(header?.trhed_runid ?? header?.trhedRunId ?? docNo ?? "");

            setHeaderData(restoredHeader);
            setLines(restoredLines.length > 0 ? restoredLines : [emptyLine(1)]);
            setCurrentDocumentNo(isProcessedDocument ? restoredDocNo : "");
            setTempNo(restoredTempNo);
            setIsReadOnlyProcessedDocument(isProcessedDocument);
            setStagedLineIds({});

            cleanSnapshotRef.current = JSON.stringify({
                selectedNo,
                headerData: restoredHeader,
                lines: restoredLines.length > 0 ? restoredLines : [emptyLine(1)],
                currentDocumentNo: isProcessedDocument ? restoredDocNo : "",
            });

            toast.showSuccess(`Loaded document ${docNo}`);
        } catch (error: any) {
            toast.showError(`Failed to load selected document: ${error?.message || error}`);
        } finally {
            setLoading(false);
            setIsLoadingDocument(false);
        }
    };

    const handleStageLine = async (line: TransactionLine, lineIndex: number) => {
        if (!selectedSetup) {
            toast.showError("No transaction setup selected");
            return;
        }

        if (!line.productCode.trim() && !line.description.trim()) {
            toast.showInfo("Enter line details before staging.");
            return;
        }

        const stagePriceMode = String(headerData.priceMode || "").trim().toLowerCase();
        const stageCost = parseNumeric(line.cost);
        const stageSelling = parseNumeric(line.selling);
        const stageAvgCost = parseNumeric(line.avgCost);

        if (stageCost < 0 || stageSelling < 0 || stageAvgCost < 0) {
            toast.showError(`Price values cannot be negative on line ${lineIndex + 1}.`);
            return;
        }

        if (stagePriceMode.startsWith("cost") && stageCost <= 0) {
            toast.showError(`Cost price must be greater than zero on line ${lineIndex + 1}.`);
            return;
        }

        if (stagePriceMode.startsWith("sell") && stageSelling <= 0) {
            toast.showError(`Selling price must be greater than zero on line ${lineIndex + 1}.`);
            return;
        }

        if (stagePriceMode.startsWith("aver") && stageAvgCost <= 0) {
            toast.showError(`Average price must be greater than zero on line ${lineIndex + 1}.`);
            return;
        }

        if (!stagePriceMode && stageCost <= 0) {
            toast.showError(`Cost price must be greater than zero on line ${lineIndex + 1}.`);
            return;
        }

        try {
            setStagingLineId(line.id);

            const result = await stageTransactionLine({
                setup_no: selectedSetup.no,
                detail: {
                    productCode: line.productCode,
                    stockCode: line.stockCode,
                    lotCode: line.lotCode,
                    description: line.description,
                    group: line.group,
                    unit: line.unit,
                    caseSize: parseFloat(line.caseSize) || 0,
                    cost: parseFloat(line.cost) || 0,
                    selling: parseFloat(line.selling) || 0,
                    avgCost: parseFloat(line.avgCost) || 0,
                    sih: parseFloat(line.sih) || 0,
                    caseQty: parseFloat(line.caseQty) || 0,
                    balanceQty: parseFloat(line.balanceQty) || 0,
                    unitQty: parseFloat(line.unitQty) || 0,
                    freeQty: parseFloat(line.freeQty) || 0,
                    expiryDate: line.expiryDate,
                    discountPer: parseFloat(line.discountPer) || 0,
                    discountAmount: parseFloat(line.discountAmount) || 0,
                    tax: parseFloat(line.tax) || 0,
                    amount: parseFloat(line.amount) || 0,
                    remarks: line.remarks,
                },
                line_no: lineIndex + 1,
                temp_no: tempNo || undefined,
            });

            if (result?.temp_no) {
                setTempNo(String(result.temp_no));
            }
            setStagedLineIds((prev) => ({ ...prev, [line.id]: true }));
            toast.showSuccess(`Line ${lineIndex + 1} staged successfully`);
        } catch (stageError: any) {
            toast.showError(stageError?.message || "Failed to stage line");
        } finally {
            setStagingLineId(null);
        }
    };

    const handleSave = async (saveMode: "temporary" | "final") => {
        try {
            if (!selectedSetup) {
                toast.showError("No transaction setup selected");
                return;
            }

            if (!selectedSetup.headertable) {
                toast.showError("Transaction setup does not have a configured header table");
                return;
            }

            setSaving(true);
            setError(null);

            const isTempDraft = Boolean(tempNo);
            const preparedHeader = {
                ...headerData,
                ...(saveMode === "final" && currentDocumentNo && !isTempDraft
                    ? { docNo: currentDocumentNo }
                    : {}),
                ...(tempNo ? { trhed_runid: tempNo } : {}),
                priceMode: headerData.priceMode || "",
                gross: computedTotals.gross,
                netDiscountAmount: computedTotals.netDiscountAmount,
                tax: computedTotals.tax,
                net: computedTotals.net,
            };

            // Prepare detail lines data
            const details = lines
                .filter((line) => line.productCode.trim() || line.description.trim())
                .map((line) => ({
                    productCode: line.productCode,
                    stockCode: line.stockCode,
                    description: line.description,
                    group: line.group,
                    unit: line.unit,
                    caseSize: parseFloat(line.caseSize) || 0,
                    cost: parseFloat(line.cost) || 0,
                    selling: parseFloat(line.selling) || 0,
                    avgCost: parseFloat(line.avgCost) || 0,
                    sih: parseFloat(line.sih) || 0,
                    caseQty: parseFloat(line.caseQty) || 0,
                    balanceQty: parseFloat(line.balanceQty) || 0,
                    unitQty: parseFloat(line.unitQty) || 0,
                    freeQty: parseFloat(line.freeQty) || 0,
                    expiryDate: line.expiryDate,
                    discountPer: parseFloat(line.discountPer) || 0,
                    discountAmount: parseFloat(line.discountAmount) || 0,
                    tax: parseFloat(line.tax) || 0,
                    amount: parseFloat(line.amount) || 0,
                    remarks: line.remarks,
                    lotCode: line.lotCode,
                }));

            if (details.length === 0) {
                const message = "At least one detail line is required.";
                setError(message);
                toast.showError(message);
                return;
            }

            for (let index = 0; index < details.length; index += 1) {
                const line = details[index];
                if (!String(line.productCode || "").trim()) {
                    const message = `Product code is required on line ${index + 1}.`;
                    setError(message);
                    toast.showError(message);
                    return;
                }

                if (parseNumeric(line.unitQty) <= 0) {
                    const message = `Quantity must be greater than zero on line ${index + 1}.`;
                    setError(message);
                    toast.showError(message);
                    return;
                }

                const priceMode = String(preparedHeader.priceMode || "").trim().toLowerCase();
                const cost = parseNumeric(line.cost);
                const selling = parseNumeric(line.selling);
                const avgCost = parseNumeric(line.avgCost);

                if (cost < 0 || selling < 0 || avgCost < 0) {
                    const message = `Price values cannot be negative on line ${index + 1}.`;
                    setError(message);
                    toast.showError(message);
                    return;
                }

                if (priceMode.startsWith("cost") && cost <= 0) {
                    const message = `Cost price must be greater than zero on line ${index + 1}.`;
                    setError(message);
                    toast.showError(message);
                    return;
                }

                if (priceMode.startsWith("sell") && selling <= 0) {
                    const message = `Selling price must be greater than zero on line ${index + 1}.`;
                    setError(message);
                    toast.showError(message);
                    return;
                }

                if (priceMode.startsWith("aver") && avgCost <= 0) {
                    const message = `Average price must be greater than zero on line ${index + 1}.`;
                    setError(message);
                    toast.showError(message);
                    return;
                }

                if (!priceMode && cost <= 0) {
                    const message = `Cost price must be greater than zero on line ${index + 1}.`;
                    setError(message);
                    toast.showError(message);
                    return;
                }
            }

            // Create or update the document (field mapping is handled in service)
            const result = await createDocument({
                setup_no: selectedSetup.no,
                header: preparedHeader,
                details: details,
                save_mode: saveMode,
            });

            const savedDocumentNo =
                saveMode === "temporary"
                    ? result?.temp_no || tempNo
                    : result?.document_no || preparedHeader.docNo || currentDocumentNo;
            if (savedDocumentNo) {
                if (saveMode === "temporary") {
                    setTempNo(String(savedDocumentNo));
                    setCurrentDocumentNo("");
                    setIsReadOnlyProcessedDocument(false);
                    setHeaderData((prev) => {
                        const rest = { ...prev };
                        delete rest.docNo;
                        return rest;
                    });
                } else {
                    setCurrentDocumentNo(String(savedDocumentNo));
                    setIsReadOnlyProcessedDocument(false);
                    setHeaderData((prev) => ({
                        ...prev,
                        docNo: String(savedDocumentNo),
                    }));
                }
            }

            if (saveMode === "temporary") {
                toast.showSuccess(`Transaction ${savedDocumentNo} temporarily saved.`);
            } else {
                toast.showSuccess(`Transaction saved successfully. Document No: ${savedDocumentNo}`);
            }

            resetTransactionDocument();
            await refreshRecentDocuments(selectedSetup);
        } catch (error: any) {
            const message = error?.message || "Failed to save transaction";
            setError(message);
            toast.showError(message);
        } finally {
            setSaving(false);
        }
    };

    const renderField = (
        field: FieldConfig,
        options?: { value?: string; disabled?: boolean },
    ) => {
        const isDisabled = isReadOnlyProcessedDocument || options?.disabled;
        const value = options?.value ?? (headerData[field.key] || "");

        return (
            <label
                className={`transaction-field${field.type === "checkbox" ? " transaction-field-checkbox" : ""}`}
                key={field.key}
            >
                <span>{field.label}</span>
                {field.type === "select" ? (
                    <select
                        disabled={isDisabled}
                        title={field.label}
                        aria-label={field.label}
                        value={value}
                        onChange={(e) => {
                            const nextValue = e.target.value;
                            updateHeaderField(field.key, nextValue);
                        }}
                    >
                        <option value="">Select...</option>
                        {(() => {
                            // Use dynamic lists for supplier and customer selects
                            if (field.key === "supplier") {
                                return suppliers.map((s) => (
                                    <option key={s.code} value={s.code}>
                                        {s.label}
                                    </option>
                                ));
                            }

                            if (field.key === "customer") {
                                return customers.map((c) => (
                                    <option key={c.code} value={c.code}>
                                        {c.label}
                                    </option>
                                ));
                            }

                            if (field.key === "assistant") {
                                return salesReps.map((rep) => (
                                    <option key={rep.id} value={String(rep.id)}>
                                        {rep.name}
                                    </option>
                                ));
                            }

                            if (field.key === "from" || field.key === "to") {
                                const options = getLocationFieldOptions(
                                    selectedSetup,
                                    field.key as "from" | "to",
                                    locationOptions,
                                    storeLocationCode,
                                );

                                const resolvedOptions = options.length > 0
                                    ? options
                                    : locationOptions.length > 0
                                        ? locationOptions
                                        : (field.options || []).map((option) => ({ value: option, label: option }));

                                return resolvedOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ));
                            }

                            return (field.options || []).map((option) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ));
                        })()}
                    </select>
                ) : field.type === "checkbox" ? (
                    <input
                        disabled={isDisabled}
                        type="checkbox"
                        title={field.label}
                        aria-label={field.label}
                        checked={headerData[field.key] === "true" || headerData[field.key] === "1"}
                        onChange={(e) => updateHeaderField(field.key, e.target.checked ? "1" : "0")}
                    />
                ) : (
                    <input
                        disabled={isDisabled || field.key === "docNo"}
                        readOnly={field.key === "docNo"}
                        type={field.type || "text"}
                        inputMode={field.type === "number" ? "decimal" : undefined}
                        title={field.label}
                        aria-label={field.label}
                        placeholder={field.label}
                        value={value}
                        onFocus={() => {
                            if (field.type === "number" && isZeroNumericString(String(value))) {
                                updateHeaderField(field.key, "");
                            }
                        }}
                        onChange={(e) =>
                            updateHeaderField(
                                field.key,
                                field.type === "number"
                                    ? sanitizeNumericString(e.target.value, { allowNegative: true })
                                    : e.target.value,
                            )
                        }
                    />
                )}
            </label>
        );
    };

    if (loading) {
        return <Loading message="Loading transaction setup..." />;
    }

    if (!selectedSetup) {
        return (
            <Box className="transaction-page" padding="medium">
                <Stack space="medium">
                    <Box className="transaction-empty-state">
                        <Text tone="secondary">No transaction setup records found.</Text>
                    </Box>
                </Stack>
            </Box>
        );
    }

    return (
        <Box className="transaction-page" padding="medium">
            <Stack space="medium">
                {error ? (
                    <Alert tone="critical">
                        <Text>{error}</Text>
                    </Alert>
                ) : null}

                <TransactionHeaderSection
                    title={selectedSetupTitle}
                    visibleHeaderFields={visibleHeaderFields}
                    renderField={renderField}
                />
                <TransactionItemsSection
                    visibleLineColumns={visibleLineColumns}
                    lines={lines}
                    isReadOnlyProcessedDocument={isReadOnlyProcessedDocument}
                    stagedLineIds={stagedLineIds}
                    stagingLineId={stagingLineId}
                    onAddLine={() => {
                        const newLineId = Date.now();
                        setLines((currentLines) => [...currentLines, emptyLine(newLineId)]);
                        setActiveLineId(newLineId);
                    }}
                    onSearchItems={openItemSearchModal}
                    onLineFocus={setActiveLineId}
                    onStageLine={handleStageLine}
                    onRemoveLine={removeLine}
                    onProductCodeChange={handleProductCodeChange}
                    onProductCodeSubmit={handleProductCodeSubmit}
                    onUpdateLine={updateLine}
                />
                {itemSearchModalOpen ? (
                    <div className="transaction-item-search-overlay" role="dialog" aria-modal="true" aria-label="Search items">
                        <div className="transaction-item-search-modal">
                            <div className="transaction-item-search-header">
                                <div className="transaction-item-search-title">
                                    <Text weight="strong" size="large">Search Items</Text>
                                    <Text tone="secondary" size="small">
                                        Search by item name, product code, stock code, or barcode
                                    </Text>
                                </div>
                                <div className="transaction-item-search-close-wrap">
                                    <Button
                                        size="small"
                                        variant="ghost"
                                        tone="critical"
                                        aria-label="Close item search"
                                        onClick={closeItemSearchModal}
                                    >
                                        ×
                                    </Button>
                                </div>
                            </div>

                            <div className="transaction-item-search-body">
                                <div className="transaction-item-search-field">
                                    <span>Search</span>
                                    <input
                                        autoFocus
                                        type="text"
                                        value={itemSearchTerm}
                                        onChange={(event) => setItemSearchTerm(event.target.value)}
                                        placeholder="Type item name, code, stock code, or barcode"
                                    />
                                </div>

                                {itemSearchTerm.trim().length < 2 ? (
                                    <div className="transaction-item-search-hint">
                                        Type at least 2 characters to search.
                                    </div>
                                ) : itemSearchLoading ? (
                                    <div className="transaction-item-search-loading">
                                        Searching items...
                                    </div>
                                ) : itemSearchResults.length > 0 ? (
                                    <div className="transaction-item-search-results">
                                        {itemSearchResults.map((item, index) => {
                                            const itemCode = getItemSearchCode(item);
                                            const title = getItemSearchTitle(item);
                                            const subText = [item.group, item.unit]
                                                .map((value) => String(value ?? "").trim())
                                                .filter(Boolean)
                                                .join(" • ");

                                            return (
                                                <button
                                                    key={`${itemCode || title || "item"}-${index}`}
                                                    type="button"
                                                    className="transaction-item-search-result-card"
                                                    onClick={() => handleSelectItemSearchResult(item)}
                                                >
                                                    <div className="transaction-item-search-result-main">
                                                        <Text weight="strong">{title}</Text>
                                                        <Text tone="secondary" size="small">
                                                            {itemCode || "No code"}
                                                            {item.stockCode ? ` | Stock: ${String(item.stockCode)}` : ""}
                                                        </Text>
                                                        {subText ? (
                                                            <Text tone="secondary" size="small">
                                                                {subText}
                                                            </Text>
                                                        ) : null}
                                                    </div>
                                                    <div className="transaction-item-search-result-meta">
                                                        <span>Case Size: {String(item.caseSize ?? "0")}</span>
                                                        <span>Cost: {String(item.cost ?? "0.00")}</span>
                                                        <span>Selling: {String(item.selling ?? "0.00")}</span>
                                                        <span>SIH: {String(getLineSih(item))}</span>
                                                    </div>
                                                    <span className="transaction-item-search-result-action">Use item</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="transaction-item-search-empty">
                                        No matching items found.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ) : null}
                <TransactionTotalsSection
                    visibleTotalFields={visibleTotalFields}
                    renderField={renderField}
                    computedTotals={computedTotals}
                    headerData={headerData}
                />
                {isEnabled(selectedSetup, "showhistory") ? (
                    <TransactionHistoryViewer
                        documents={documents}
                        setupNo={selectedSetup.no}
                        setupTitle={selectedSetupTitle}
                        onLoadDocument={handleRestoreSelectedDocument}
                    />
                ) : null}
                <TransactionPaymentSection renderField={renderField} isEnabled={isEnabled(selectedSetup, "payment")} />
                <TransactionActionsBar
                    saving={saving}
                    isReadOnlyProcessedDocument={isReadOnlyProcessedDocument}
                    onNew={resetTransactionDocument}
                    onTemporarySave={() => (requireFormChanges("temporary save") ? handleSave("temporary") : undefined)}
                    onProcess={() => (requireFormChanges("saving") ? handleSave("final") : undefined)}
                    onPrint={() => {
                        const canPrintWithoutChanges = isReadOnlyProcessedDocument && Boolean(currentDocumentNo);
                        if (!canPrintWithoutChanges && !requireFormChanges("printing")) {
                            return;
                        }

                        (async () => {
                            try {
                                if (!currentDocumentNo) {
                                    toast.showWarning("Please save the document first");
                                    return;
                                }
                                if (!selectedSetup) {
                                    toast.showError("Setup not found");
                                    return;
                                }
                                toast.showInfo("Generating report...");
                                const reportType = selectedSetup.reportname?.trim() || "";

                                if (!reportType) {
                                    toast.showError("No report name is configured for this transaction setup");
                                    return;
                                }

                                await generateTransactionReportPdf(selectedSetup.no, currentDocumentNo, reportType);
                                toast.showSuccess("Report downloaded");
                            } catch (error: any) {
                                toast.showError(error.message || "Failed to generate report");
                            }
                        })();
                    }}
                />
            </Stack>
        </Box>
    );
};

export default TransactionScreen;
